import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { authAPI, SESSION_MAX_AGE_MS } from '../services/adminApi';

const AuthContext = createContext(null);

function clearStoredSession() {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_refresh_token');
  localStorage.removeItem('admin_user');
  localStorage.removeItem('admin_login_at');
}

function isSessionExpired() {
  const loginAt = Number(localStorage.getItem('admin_login_at'));
  return !loginAt || (Date.now() - loginAt) > SESSION_MAX_AGE_MS;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    if (isSessionExpired()) {
      clearStoredSession();
      return null;
    }
    try { return JSON.parse(localStorage.getItem('admin_user') || 'null'); } catch { return null; }
  });

  const logoutTimerRef = useRef(null);

  const logout = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    clearStoredSession();
    setUser(null);
  }, []);

  const scheduleAutoLogout = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    const loginAt = Number(localStorage.getItem('admin_login_at'));
    if (!loginAt) return;
    const remaining = SESSION_MAX_AGE_MS - (Date.now() - loginAt);
    if (remaining <= 0) { logout(); return; }
    logoutTimerRef.current = setTimeout(logout, remaining);
  }, [logout]);

  useEffect(() => {
    if (user) scheduleAutoLogout();
    return () => { if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current); };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    const token        = data.token || data.access_token || data.session?.access_token;
    const refreshToken = data.refresh_token || data.session?.refresh_token;
    const profile       = data.user  || data.profile;

    if (!profile || !['admin', 'superadmin'].includes(profile.role)) {
      throw { response: { data: { error: 'This account does not have admin access.' } } };
    }

    localStorage.setItem('admin_token', token);
    if (refreshToken) localStorage.setItem('admin_refresh_token', refreshToken);
    localStorage.setItem('admin_user', JSON.stringify(profile));
    localStorage.setItem('admin_login_at', String(Date.now()));

    setUser(profile);
    scheduleAutoLogout();
    return profile;
  }, [scheduleAutoLogout]);

  return (
    <AuthContext.Provider value={{ user, login, logout, isSuperAdmin: user?.role === 'superadmin' }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);